import { useEffect, useState } from 'react';
import { Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import useSheetAnim from '../hooks/useSheetAnim';
import useVoucherViewer from '../hooks/useVoucherViewer';
import { Skeletons, RM } from './kit';
import { payoutDate } from '../lib/payout';
import * as earnApi from '../api/earnings';

const label = { fontSize: 11.5, fontWeight: 600, letterSpacing: '.08em', color: 'var(--muted)' };
const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14 };
const mono = { fontFamily: 'ui-monospace,monospace' };

/**
 * One of the agent's payment vouchers from ERP213 — the system before CIDAS. Opened
 * from "Older payments" on Earnings. History only: what it paid is already in the
 * sale's Paid figure, so the sheet says so instead of offering anything to act on.
 * Same bottom sheet as PayoutSheet; `row` is the list row, so the header draws at once.
 */
export default function Erp213VoucherSheet({ open, voucherId: idProp, row, onClose }) {
  const nav = useNavigate();
  const { mounted, closing } = useSheetAnim(open);
  // The id is cleared upstream while the exit animation still plays.
  const [voucherId, setVoucherId] = useState(idProp);
  if (idProp && idProp !== voucherId) setVoucherId(idProp);
  const pv = useVoucherViewer();
  const [d, setD] = useState(null);
  const [failed, setFailed] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!open || !voucherId) return undefined;
    let live = true;
    setD(null);
    setFailed(null);
    earnApi.getErp213Voucher(voucherId)
      .then((r) => { if (live) setD(r?.data ?? r); })
      .catch((e) => { if (live) setFailed(e); });
    return () => { live = false; };
  }, [open, voucherId, tick]);

  if (!mounted) return null;

  const v = d || (row?.id === voucherId ? row : null);
  const lines = Array.isArray(d?.lines) ? d.lines : [];
  const files = Array.isArray(d?.files) ? d.files : [];
  const wht = Number(v?.wht_amount) || 0;
  const openFile = (f) => pv.open(f.id, () => earnApi.getErp213VoucherFile(voucherId, f.id)
    .then((r) => ({ ...(r?.data ?? r), doc_no: v?.pv_no })));

  return (
    <>
      <div
        onClick={onClose}
        className={`sheet-scrim${closing ? ' is-closing' : ''}`}
        style={{ position: 'fixed', inset: 0, zIndex: 4600, background: 'rgba(0,0,0,.45)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className={`sheet-panel${closing ? ' is-closing' : ''}`}
          style={{ background: 'var(--surface)', borderRadius: '18px 18px 0 0', maxHeight: '86%', display: 'flex', flexDirection: 'column' }}
        >
          <div style={{ flex: 'none', padding: '10px 16px 14px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ width: 38, height: 4, borderRadius: 999, background: 'var(--border)', margin: '0 auto 13px' }} />
            {!v ? (failed ? <div style={label}>ERP213 VOUCHER</div> : <Skeletons n={1} height={58} />) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                  <span style={label}>PAID IN ERP213 · {payoutDate(v.pv_date).toUpperCase()}</span>
                  <span style={{ ...mono, fontSize: 12, color: 'var(--muted)' }}>{v.pv_no}</span>
                </div>
                <div style={{ fontSize: 24, fontWeight: 700, marginTop: 4, fontVariantNumeric: 'tabular-nums', color: 'var(--ok)' }}>
                  {RM(v.amount)}
                </div>
                {wht > 0 && (
                  <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                    Includes {RM(wht)} withholding tax
                  </div>
                )}
                {d?.bank_name && (
                  <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>
                    To {d.bank_name}{d.bank_account_last4 ? ` ····${d.bank_account_last4}` : ''}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '14px 16px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {failed?.status === 404 ? (
              <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5 }}>This voucher could not be found.</div>
            ) : (
              <>
                <div style={{ ...card, padding: '12px 14px', fontSize: 12, color: 'var(--muted)', lineHeight: 1.5, background: 'var(--surface2)' }}>
                  HQ paid this from ERP213, the system before CIDAS. It is already counted in the sale&rsquo;s Paid figure.
                </div>

                <div style={label}>WHAT IT PAID</div>
                {failed ? (
                  <div style={{ ...card, marginTop: -4, padding: '13px 15px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                    <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Couldn&rsquo;t load this voucher just now.</span>
                    <Button size="small" onClick={() => setTick((t) => t + 1)}>Try again</Button>
                  </div>
                ) : !d ? (
                  <Skeletons n={2} height={54} />
                ) : (
                  <div style={{ ...card, marginTop: -4, overflow: 'hidden' }}>
                    {lines.map((l, i) => (
                      <div
                        key={l.id}
                        role={l.sale_id ? 'button' : undefined}
                        tabIndex={l.sale_id ? 0 : undefined}
                        onClick={l.sale_id ? () => nav(`/sales/${l.sale_id}`) : undefined}
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, padding: '13px 15px', borderBottom: i < lines.length - 1 ? '1px solid var(--border)' : 'none', cursor: l.sale_id ? 'pointer' : 'default' }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 600, ...mono }}>{l.sale_ref || '—'}</div>
                          <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.45, marginTop: 2, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-line' }}>
                            {String(l.description || '').replace(/\r/g, '\n')}
                          </div>
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{RM(l.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {d && (
                  <>
                    <div style={label}>PAYMENT VOUCHER</div>
                    {files.length ? (
                      <div style={{ ...card, marginTop: -4, overflow: 'hidden' }}>
                        {files.map((f, i) => (
                          <div
                            key={f.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => openFile(f)}
                            style={{ padding: '13px 15px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', borderBottom: i < files.length - 1 ? '1px solid var(--border)' : 'none' }}
                          >
                            <span style={{ width: 34, height: 34, borderRadius: 9, flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'color-mix(in srgb,#cf1322 10%,var(--surface))', color: '#cf1322', fontSize: 10, fontWeight: 800 }}>
                              {/\.pdf$/i.test(f.file_name) ? 'PDF' : 'IMG'}
                            </span>
                            <div style={{ flex: 1, minWidth: 0, fontSize: 12.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.file_name}</div>
                            <span style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1 }}>›</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ ...card, marginTop: -4, padding: '13px 15px', fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5 }}>
                        No scan was uploaded to ERP213 for this voucher — the number and amount are the record.
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          <div style={{ padding: '15px 16px', paddingBottom: 'calc(12px + env(safe-area-inset-bottom))', borderTop: '1px solid var(--border)', flex: 'none' }}>
            {files.length ? (
              <Button type="primary" block loading={pv.busy === files[0].id} onClick={() => openFile(files[0])} style={{ height: 46, fontWeight: 600 }}>
                View payment voucher
              </Button>
            ) : (
              <Button block onClick={onClose}>Close</Button>
            )}
          </div>
        </div>
      </div>
      {pv.viewer}
    </>
  );
}
